import { Button } from '@/components/common/Button';
import Divider from '@/components/common/Divider';
import PortalEntry from '@/components/common/PortalEntry';
import FormStepper from '@/components/form/components/FormStepper';
import { getDefaultCurrencyId } from '@/components/form/schema/common';
import {
    PortalCryptoDepositFormInput,
    PortalCryptoDepositFormValues,
    portalCryptoDepositSchema
} from '@/components/form/schema/portalCryptoDepositSchema';
import ErrorMessage from '@/components/inputs/ErrorMessage';
import FilterSelect from '@/components/inputs/FilterSelect';
import Input from '@/components/inputs/Input';
import PortalInputController from '@/components/inputs/PortalInputController';
import PortalNumberInput from '@/components/inputs/PortalNumberInput';
import Select from '@/components/inputs/Select';
import TextArea from '@/components/inputs/TextArea';
import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { ErrorMsg } from '@/types/common';
import useAbortController from '@/utils/hooks/useAbortController';
import useCurrencyOptions from '@/utils/hooks/useCurrencyOptions';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import useToast from '@/utils/hooks/useToast';
import useWalletOptions from '@/utils/hooks/useWalletOptions';
import { useSso } from '@/utils/providers/SSOProvider';
import { Tab } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { PortalDepositModalProps } from '.';
import { WhitelistError } from '../PortalInternalTransferModal';
import SSIProfileWalletDetails from './SSIProfileWalletDetails';

const defaultWalletId = { label: 'Select your whitelisted wallet', value: undefined };
const defaultCurrencyId = getDefaultCurrencyId('deposit', 'asset');

export const defaultValues: Partial<PortalCryptoDepositFormInput> = {
    amount: undefined,
    currencyId: defaultCurrencyId,
    walletId: defaultWalletId,
    requestDescription: '',
    transactionHash: ''
};

const CryptoDepositForm = (props: PortalDepositModalProps) => {
    const { opened, mode, currency, handlers, onRefresh } = props;

    const lang = useLanguage();
    const [toastError, toastSuccess] = useToast();

    const [tabIndex, setTabIndex] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<ErrorMsg>({ message: '' });

    const steps = useMemo(() => {
        return [
            { key: 1, label: lang.portal.requestDetails },
            { key: 2, label: lang.portal.supportDetails }
        ];
    }, [lang]);

    const form = useForm<PortalCryptoDepositFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(portalCryptoDepositSchema({}))
    });
    const {
        reset,
        watch,
        setValue,
        handleSubmit,
        formState: { isValid }
    } = form;

    const [selectedWalletId, selectedCurrency] = watch(['walletId', 'currencyId']);

    const { on401Error } = use401Error();
    const { jwt, foundEntity, ssiProfileWalletInfoApi } = useSso();
    const { isCurrenciesLoading, cryptoDepositCcyOptions } = useCurrencyOptions();
    const { walletOptions, walletDetails } = useWalletOptions(selectedCurrency, selectedWalletId);
    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const { data: ssiProfileWallet } = ssiProfileWalletInfoApi;

    const hasAccount = useMemo(() => {
        return selectedCurrency?.value && walletOptions?.length;
    }, [selectedCurrency, walletOptions]);

    const foundSSIProfiles = useMemo(() => {
        if (ssiProfileWallet && ssiProfileWallet?.length > 0 && foundEntity) {
            const profile = ssiProfileWallet.find((profile) => profile.entityId === foundEntity.parentId);
            if (profile) {
                return profile.profileInfos.filter(
                    (info) =>
                        info.supportedAssets.find((asset) => asset.currencyId === selectedCurrency?.value) &&
                        info.networkId === walletDetails?.networkId &&
                        info.active
                );
            }
        }
    }, [ssiProfileWallet, walletDetails, selectedCurrency, foundEntity]);

    const uploadTransactionHash = useCallback(async (requestId: number, entityId: number, transactionHash: string) => {
        const payload = {
            id: requestId,
            entityId,
            transactionHash
        };
        if (portalURL) {
            return await axios.put(portalAPI('/api/requests/upload-transaction-hash'), payload, {
                headers: { Authorization: `Bearer ${jwt}` }
            });
        }
    }, []);

    const onSubmit = async (data: PortalCryptoDepositFormInput) => {
        setIsLoading(true);
        const dataValidated = data as PortalCryptoDepositFormValues;
        try {
            if (foundEntity) {
                let depositResponse = {} as any;
                const payload = {
                    entityId: foundEntity.entityId,
                    amount: parseFloat(dataValidated.amount),
                    walletId: dataValidated.walletId.value,
                    requestDescription: dataValidated.requestDescription || null,
                    currencyId: dataValidated.currencyId.value
                };
                if (portalURL) {
                    depositResponse = await axios.post(portalAPI('/api/requests/deposit/crypto'), payload, {
                        headers: { Authorization: `Bearer ${jwt}` },
                        signal: abortController.signal
                    });
                }
                if (data.transactionHash && depositResponse?.id)
                    await uploadTransactionHash(depositResponse.data.id, foundEntity.entityId, data.transactionHash);

                const title = 'Crypto Deposit';
                const body = 'Deposit request submitted successfully';
                toastSuccess({ body, title });
                onRefresh?.();
            }
            handlers.close();
        } catch (err: any) {
            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on /api/requests/deposit/crypto. JWT may be invalid or expired.`
                );
            });
            handleApiError(err);
        }
        setIsLoading(false);
    };

    const onError = (e: FieldErrors) => console.log(e);

    const handleApiError = useCallback((error) => {
        setIsLoading(false);
        setError(error?.response?.data || error);
    }, []);

    useEffect(() => {
        if (walletOptions && walletOptions.length > 0 && walletOptions.length < 2) {
            setValue('walletId', walletOptions[0], { shouldValidate: true });
        }
    }, [walletOptions]);

    useEffect(() => {
        if (selectedCurrency?.value) {
            setTabIndex(0);
            let refreshBankInfoId: any = defaultWalletId;
            if (walletOptions.length > 0) refreshBankInfoId = walletOptions[0];
            reset({
                ...defaultValues,
                walletId: refreshBankInfoId,
                currencyId: selectedCurrency
            });
        }
    }, [selectedCurrency?.value]);

    useEffect(() => {
        if (selectedCurrency?.value) {
            if (!hasAccount || !walletOptions.length) {
                setError({ message: <WhitelistError type="CRYPTO" /> });
            } else {
                setError({ message: '' });
            }
        }
    }, [walletOptions, selectedCurrency, setValue]);

    useEffect(() => {
        const prefillCcy = cryptoDepositCcyOptions.find((option) => option.name === currency);
        if (prefillCcy) setValue('currencyId', prefillCcy, { shouldValidate: true });
    }, []);

    useEffect(() => {
        if (opened) {
            ssiProfileWalletInfoApi.mutate();
            setAbortController(new AbortController());
        } else abortController.abort();

        return () => abortController.abort();
    }, [opened]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="relative h-full w-full text-brand-text">
                <FormStepper.Main activeStep={tabIndex} onStep={(step) => setTabIndex(step)}>
                    {steps.map(({ label }, index) => (
                        <FormStepper.Step key={label} step={index} label={label} />
                    ))}
                </FormStepper.Main>
                <Tab.Group defaultIndex={0} selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
                    <Tab.List className="hidden">
                        {steps.map(({ label }) => (
                            <Tab key={label} className="text-sm">
                                {label}
                            </Tab>
                        ))}
                    </Tab.List>
                    <Tab.Panels className="flex-1 basis-0 sm:flex-auto pb-4 w-full h-[500px] overflow-y-auto px-2 sm:px-4">
                        <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                            <fieldset className="space-y-3">
                                <legend className="text-xs text-brand-neutral-500">
                                    {lang.portal.depositInformation}
                                </legend>
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="currencyId"
                                            label={mode === 'bank' ? lang.lite.currency : lang.lite.asset}
                                            asyncInput={!jwt || isCurrenciesLoading}
                                            placeholder={mode === 'bank' ? 'Currency' : 'Asset'}
                                            required>
                                            <FilterSelect
                                                options={cryptoDepositCcyOptions}
                                                enableCurrencyIcon
                                                enableSorting
                                            />
                                        </PortalInputController>
                                    </div>
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="amount"
                                            label={lang.portal.amountToDeposit}
                                            placeholder="0.00"
                                            required>
                                            <PortalNumberInput currency={selectedCurrency?.name || ''} />
                                        </PortalInputController>
                                    </div>
                                </div>
                            </fieldset>
                            <fieldset className="space-y-3">
                                <legend className="text-xs text-brand-neutral-500">{lang.portal.walletDetails}</legend>
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="walletId"
                                            label={lang.portal.cryptoWallet}
                                            required>
                                            <Select options={walletOptions} />
                                        </PortalInputController>
                                    </div>
                                    {walletDetails && (
                                        <div className="flex-1">
                                            <PortalEntry
                                                label={lang.portal.network}
                                                value={walletDetails.networkName}
                                                disabled
                                            />
                                        </div>
                                    )}
                                </div>
                                {walletDetails && (
                                    <PortalEntry
                                        label={lang.portal.walletAddress}
                                        value={walletDetails.walletAddress}
                                        disabled
                                    />
                                )}
                            </fieldset>

                            <PortalInputController
                                name="requestDescription"
                                label={lang.portal.requestDescription}
                                placeholder={lang.portal.requestDescription}>
                                <TextArea rows={2} />
                            </PortalInputController>
                            <ErrorMessage error={error} />
                            {selectedCurrency?.value &&
                                !error.message &&
                                foundSSIProfiles &&
                                foundSSIProfiles.length > 0 && (
                                    <SSIProfileWalletDetails ssiProfiles={foundSSIProfiles} />
                                )}
                        </Tab.Panel>

                        <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                            <fieldset className="space-y-3">
                                <legend className="text-xs text-brand-neutral-500">
                                    {lang.portal.supportingInformationOptional}
                                </legend>
                                <PortalInputController
                                    name="transactionHash"
                                    label={lang.portal.transactionHash}
                                    placeholder="Enter Transaction Hash">
                                    <Input />
                                </PortalInputController>
                            </fieldset>
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
                <Divider neutral />
                <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                    <button
                        type="button"
                        className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                        onClick={() => {
                            if (tabIndex === 1) setTabIndex(0);
                            else if (tabIndex === 0) onClose();
                        }}>
                        {tabIndex === 0 ? lang.common.cancel : lang.portal.previousStep}
                    </button>
                    <Button
                        type="button"
                        className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                        onClick={() => {
                            if (tabIndex === 0) setTabIndex(1);
                            else handleSubmit(onSubmit, onError)();
                        }}
                        disabled={!isValid && tabIndex === 1}
                        isLoading={isLoading}>
                        {tabIndex === 0 ? lang.portal.nextStep : lang.portal.submitRequest}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default CryptoDepositForm;
